import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CornerDownRight, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import type { Tarea, TipoTarea } from "./TarjetaTarea"

/**
 * Las subtareas de una macro, con su alta.
 *
 * ⚠ **Dos niveles, como el backlog del 2.0.** Una subtarea no puede tener subtareas:
 * el servidor lo rechaza y aquí ni se ofrece. Un árbol de profundidad libre obliga a
 * decidir qué significa cerrar una rama —¿arrastra a los nietos?, ¿los deja
 * huérfanos?— y nadie necesita eso para repartir trabajo.
 *
 * ⚠ **Sin dueño, la subtarea hereda el de la macro.** Lo hace el servidor, y por eso
 * este formulario no pregunta a quién asignarla: lo normal es que la haga quien lleva
 * la macro, y preguntarlo cada vez convierte «añadir un paso» en rellenar una ficha.
 * Reasignarla después es un clic en su propio panel.
 */

const COLOR_ESTADO: Record<string, string> = {
  HECHA: "var(--success)",
  CANCELADA: "var(--fg-muted)",
}

type Props = {
  macroId: number
  /** El tipo de la macro: una subtarea de una `DEV` es `DEV` salvo que se diga otra cosa. */
  tipo: TipoTarea
  subtareas: Tarea[]
  puedeCrear: boolean
  onAbrir: (id: number) => void
  onCreada: () => void
}

export function SubtareasDe({ macroId, tipo, subtareas, puedeCrear, onAbrir, onCreada }: Props) {
  const clienteQuery = useQueryClient()
  const [titulo, setTitulo] = useState("")
  const [anadiendo, setAnadiendo] = useState(false)

  const crear = useMutation({
    mutationFn: () =>
      api.post("/api/v1/tareas", {
        titulo: titulo.trim(),
        tipo,
        tarea_padre_id: macroId,
      }),
    onSuccess: () => {
      setTitulo("")
      setAnadiendo(false)
      clienteQuery.invalidateQueries({ queryKey: ["tareas"] })
      onCreada()
    },
  })

  const listo = titulo.trim().length >= 3
  const borde = { borderColor: "var(--border)" }

  return (
    <section className="mt-5">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <CornerDownRight size={14} aria-hidden={true} />
        Subtareas
        <span className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
          {subtareas.filter((s) => s.cerrado_en !== null).length}/{subtareas.length}
        </span>
      </h3>

      {subtareas.length === 0 ? (
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Sin subtareas.
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {subtareas.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                data-testid={`subtarea-${s.id}`}
                onClick={() => onAbrir(s.id)}
                className="flex w-full items-baseline gap-2 rounded-[var(--radius-control)] px-1 py-1 text-left text-sm hover:underline"
              >
                <span className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
                  {s.codigo}
                </span>
                <span
                  className="flex-1"
                  style={{
                    // Tachada al cerrarse: el estado de una lista de pasos se lee de
                    // un vistazo o no se lee.
                    textDecoration: s.cerrado_en !== null ? "line-through" : undefined,
                    color: COLOR_ESTADO[s.estado],
                  }}
                >
                  {s.titulo}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {puedeCrear &&
        (anadiendo ? (
          <form
            className="mt-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (listo) crear.mutate()
            }}
          >
            <label className="block text-xs" htmlFor={`subtarea-${macroId}`}>
              Nueva subtarea
              <input
                id={`subtarea-${macroId}`}
                className="campo mt-1 text-sm"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Recoger los documentos en la aduana"
              />
            </label>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAnadiendo(false)
                  setTitulo("")
                }}
                className="rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                style={borde}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!listo || crear.isPending}
                className="rounded-[var(--radius-control)] px-2 py-1 text-xs disabled:opacity-50"
                style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
              >
                {crear.isPending ? "Añadiendo…" : "Añadir"}
              </button>
            </div>
            {crear.isError && (
              <p role="alert" className="mt-2 text-xs" style={{ color: "var(--destructive)" }}>
                {crear.error instanceof ErrorApi
                  ? crear.error.message
                  : "No se pudo crear la subtarea."}
              </p>
            )}
          </form>
        ) : (
          <button
            type="button"
            data-testid="anadir-subtarea"
            onClick={() => setAnadiendo(true)}
            className="mt-2 flex items-center gap-1 rounded-[var(--radius-control)] border border-dashed px-2 py-1 text-xs"
            style={{ ...borde, color: "var(--fg-muted)" }}
          >
            <Plus size={12} aria-hidden={true} />
            Añadir subtarea
          </button>
        ))}
    </section>
  )
}
